import DOMPurify from 'dompurify';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { Feedback, useFeedback } from '@bonusly/common-feedback';
import { RelativeTime } from '@bonusly/common-relative-time';
import { Box, Button, ChatAltSolid, createUseVariantStyle, Flex, HStack, Spacer, StyleProps, Text, VStack } from '@bonusly/design-system';
import { useUser } from '@bonusly/user-queries';

import { ReferenceUnlinkButton } from '../reference-unlink-button/reference-unlink-button';

export interface FeedbackCardV2Props extends StyleProps {
	// Backward compatible: support both object and ID
	feedback?: Feedback;
	feedbackId?: string;
	onUnlinkFeedback?: (feedbackId: string) => void;
	sectionTitle?: string;
	variant?: 'flat' | 'default'; // TODO: Add compact variant
	showThreadCount?: boolean;
	// Link to entity functionality (goals, topics, etc.)
	showLinkButton?: boolean;
	isLinked?: boolean;
	isLinkLoading?: boolean;
	onLink?: (feedbackId: string) => void;
	onUnlink?: (feedbackId: string) => void;
}

const useWrapperStyles = createUseVariantStyle({
	variant: {
		flat: {
			border: '1px solid',
			borderColor: 'border.default',
			boxShadow: 'none',
		},
		default: {
			border: 'none',
			boxShadow: 'sm',
		},
	},
});

export const FeedbackCardV2: FC<FeedbackCardV2Props> = ({
	feedback: feedbackProp,
	feedbackId,
	sectionTitle,
	onUnlinkFeedback,
	showThreadCount = false,
	showLinkButton = false,
	isLinked = false,
	isLinkLoading = false,
	onLink,
	onUnlink,
	variant = 'default',
	...rest
}) => {
	const { t } = useTranslation(['one_on_ones', 'common']);

	// Fetch feedback by ID if only ID is provided
	const { data: fetchedFeedback, isLoading: isFeedbackLoading } = useFeedback(feedbackId);

	// Use provided feedback object or fetched feedback
	const feedback = feedbackProp || fetchedFeedback;

	const { data: giver } = useUser(feedback?.lastResponse()?.userId);
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const wrapperStyles = useWrapperStyles({ variant });

	// Show loading if we're fetching feedback by ID
	if (feedbackId && isFeedbackLoading) {
		return (
			<Box bg="bg.surface" px={4} py={3} borderRadius="md" boxShadow="sm" sx={{ breakInside: 'avoid' }} {...rest}>
				<Text color="text.softest">Loading feedback...</Text>
			</Box>
		);
	}

	if (!feedback || !feedback.completedAt || feedback.messages.length === 0 || !giver) {
		return null;
	}

	searchParams.set('evaluation_id', feedback.id);
	const lastResponse = feedback.lastResponse();

	if (!lastResponse || lastResponse.text === '') {
		return null;
	}

	const text = DOMPurify.sanitize(lastResponse.text, {
		ALLOWED_TAGS: [],
	});

	const hasRequest = Boolean(feedback?.request?.text);
	const threadLength = feedback.messages.length + (hasRequest ? 1 : 0);
	const threadText = threadLength > 1 ? t('meeting.feedback_footer.thread_count', { count: threadLength }) : '';

	return (
		<Box
			as={Link}
			bg="bg.surface"
			borderRadius="md"
			display="block"
			p={3}
			sx={{ breakInside: 'avoid' }}
			to={`${location.pathname}?${searchParams.toString()}`}
			{...wrapperStyles}
			{...rest}
		>
			<VStack alignItems="flex-start">
				<HStack alignItems="center" width="full">
					<Box h={8} w={8} borderRadius="full" bg="hashtag.bgAlpha" display="flex" alignItems="center" justifyContent="center">
						<ChatAltSolid color="hashtag.fgOnSurface" size={5} />
					</Box>

					<Text size="sm" fontWeight="semibold" color="text.default">
						{t('meeting.carousel.feedback')}
					</Text>

					<Spacer />

					<RelativeTime color="text.softest" date={feedback.createdAt} fontSize="sm" />

					{onUnlinkFeedback && sectionTitle && (
						<ReferenceUnlinkButton onClickUnlinkReference={() => onUnlinkFeedback(feedback.id)} sectionTitle={sectionTitle} />
					)}
				</HStack>

				<Box maxHeight="110px" overflow="hidden" wordBreak="break-word" maxW="100%">
					<Text
						size="sm"
						color="text.default"
						ml={1}
						fontFamily="ui"
						textOverflow="ellipsis"
						whiteSpace="pre-wrap"
						style={{
							WebkitBoxOrient: 'vertical',
							WebkitLineClamp: 4,
						}}
					>
						<Text as="span" fontWeight="semibold" size="sm" color="text.default" mr={1}>
							{giver.displayName}:
						</Text>
						{text}
					</Text>
				</Box>

				{showThreadCount && threadText && (
					<Flex dir="row" alignItems="center">
						<Text color="text.softer" size="sm">
							{threadText}
						</Text>
					</Flex>
				)}

				{showLinkButton && (
					<Flex justifyContent="center" pt={3} width="full">
						<Button
							size="sm"
							variant="alpha"
							colorScheme="primary"
							bg={isLinked ? 'error.bg' : 'primary.bgAlpha'}
							color={isLinked ? 'error.text' : 'primary.fg'}
							_hover={{
								bg: isLinked ? 'error.closeBg' : 'primary.bgAlphaHover',
								color: isLinked ? 'error.text' : 'primary.fg',
							}}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								if (isLinked) {
									onUnlink?.(feedback.id);
								} else {
									onLink?.(feedback.id);
								}
							}}
							width="full"
							isLoading={isLinkLoading}
							loadingText={isLinked ? 'Removing...' : 'Adding...'}
							disabled={isLinkLoading}
						>
							{isLinked ? 'Remove' : 'Add'}
						</Button>
					</Flex>
				)}
			</VStack>
		</Box>
	);
};
